import React, { useState, useEffect } from 'react';
import { health as healthApi } from '../../api/client';
import { Heart, RefreshCw, Loader2, AlertCircle, TrendingUp, Sparkles } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function HealthPage({ activeBrandId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const result = await healthApi.get(activeBrandId);
      setData(result);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [activeBrandId]);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  }

  const scoreColor = data.overall >= 70 ? 'text-emerald-600' : data.overall >= 40 ? 'text-amber-600' : 'text-rose-600';
  const scoreLabel = data.overall >= 70 ? 'Excellent' : data.overall >= 40 ? 'Good' : 'Needs Improvement';

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Health</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Brand Health Score</h1>
        </div>
        <button onClick={loadData} className="px-3 py-1.5 text-sm border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-600 flex items-center gap-1">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Score Display */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="text-center">
            <div className={`font-serif text-7xl ${scoreColor}`}>{data.overall}</div>
            <div className="text-stone-500 mt-1">/100</div>
          </div>
          <div className="flex-1">
            <h2 className="font-serif text-2xl text-stone-900 dark:text-white mb-2">{scoreLabel}</h2>
            <p className="text-stone-500 mb-4">Your brand health score across all AI models</p>
            <div className="grid grid-cols-5 gap-3">
              {[
                { name: 'ChatGPT', key: 'chatgpt', color: 'bg-emerald-500' },
                { name: 'Perplexity', key: 'perplexity', color: 'bg-violet-500' },
                { name: 'Claude', key: 'claude', color: 'bg-amber-500' },
                { name: 'Gemini', key: 'gemini', color: 'bg-blue-500' },
                { name: 'Google AI', key: 'google_aio', color: 'bg-rose-500' },
              ].map(llm => (
                <div key={llm.key} className="text-center">
                  <div className="text-xs text-stone-500 mb-1">{llm.name}</div>
                  <div className="font-serif text-lg text-stone-900 dark:text-white">{Math.max(0, Math.min(100, data.breakdown?.[llm.key] || 0))}%</div>
                  <div className="h-1.5 bg-stone-100 dark:bg-stone-700 mt-1 overflow-hidden">
                    <div className={`h-full ${llm.color}`} style={{ width: `${Math.max(0, Math.min(100, data.breakdown?.[llm.key] || 0))}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Score Over Time</h2>
        <div className="h-64" style={{ minHeight: '256px', minWidth: '200px' }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={256}>
            <AreaChart data={data.trend || []}>
              <defs>
                <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#a8a29e" fontSize={12} tickLine={false} axisLine={false}
                tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
              <YAxis stroke="#a8a29e" fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} />
              <Tooltip />
              <Area type="monotone" dataKey="score" stroke="#10b981" fill="url(#healthGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-amber-500" />
          <h2 className="font-serif text-xl text-stone-900 dark:text-white">Recommendations</h2>
        </div>
        <div className="space-y-3">
          {(data.recommendations || []).map((rec, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-stone-700 dark:text-stone-300">{rec}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
